import React, { useState, useEffect, useRef } from 'react';
import { dataCy, SELECTORS } from '../utils/dataCy';

const SearchBar = ({ categories, currentLang, t }) => {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const containerRef = useRef(null);

  // Función para manejar la búsqueda
  const handleSearch = (rawQuery) => {
    const trimmedQuery = rawQuery.trim(); // Elimina espacios al inicio y al final
    if (!trimmedQuery || !categories || !Array.isArray(categories)) {
      setSearchResults([]);
      return;
    }

    const results = [];
    const keywords = trimmedQuery.toLowerCase().split(/\s+/); // Divide en palabras clave ignorando múltiples espacios

    categories.forEach((category) => {
      if (category && category.articles && Array.isArray(category.articles)) {
        category.articles.forEach((article) => {
        const articleTitle = article.title.toLowerCase();
        // Verifica si todas las palabras clave están presentes en el título
        if (keywords.every((keyword) => articleTitle.includes(keyword))) {
          results.push({
            title: article.title,
            category: category.title,
            slug: `/${category.slug}/${article.slug}`,
          });
        }
        });
      }
    });

    setSearchResults(results);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    handleSearch(value);
  };

  // Cerrar resultados al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setQuery('');
        setSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para resaltar coincidencias
  const highlightText = (text, query) => {
    if (!query) return text;

    const trimmedQuery = query.trim(); // Elimina espacios innecesarios
    const regex = new RegExp(`(${trimmedQuery.split(/\s+/).join('|')})`, 'gi');
    const parts = text.split(regex); // Divide el texto en partes: coincidencias y no coincidencias.

    return parts.map((part, index) =>
      trimmedQuery.toLowerCase().includes(part.toLowerCase()) ? (
        <span key={index} className="text-greenExcent font-bold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Barra de búsqueda */}
      <input
        type="text"
        placeholder={t('searchPlaceholder', 'Search articles...')}
        value={query}
        onChange={handleChange}
        className="w-full p-3 border border-gray-300 rounded-md text-black dark:text-white dark:bg-grayButton dark:border-grayButtonBorder placeholder-gray-400 dark:placeholder-gray-200"
        data-cy={dataCy(SELECTORS.INPUT, 'home-search')}
      />

      {/* Resultados de búsqueda */}
      {query.trim() && ( // Verifica que la consulta no sea solo espacios
        <div className="absolute mt-2 bg-white dark:bg-grayButtonSolid border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg w-full max-h-96 overflow-y-auto z-10">
          {searchResults.length > 0 ? (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {searchResults.map((result, index) => (
                <li
                  key={index}
                  className="p-3 hover:bg-gray-100 dark:hover:bg-primaryBg cursor-pointer"
                  onClick={() => {
                    const theme = localStorage.getItem('theme');
                    if (theme === 'dark') {
                      document.documentElement.classList.add('dark');
                    } else {
                      document.documentElement.classList.remove('dark');
                    }
                    window.location.href = `/${currentLang}${result.slug}`;
                  }}
                  data-cy={dataCy(SELECTORS.BUTTON, `search-result-${index}`)}
                >
                  <h3 className="text-lg text-left font-bold text-gray-800 dark:text-gray-200">
                    {highlightText(result.title, query)}
                  </h3>
                  <p className="text-sm text-left text-gray-600 dark:text-gray-400">
                    {result.category}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="p-3 text-gray-500 dark:text-gray-400">
              {t('noResults', 'No results found')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
